/**
 * All user-visible failures travel as an AppError carrying a stable `code` plus
 * parameters. The code is looked up in src/i18n at display time, so the same failure
 * renders in Korean or English without the core layer knowing about either language.
 */

export type AppErrorCode =
  // file intake
  | 'notPdf'
  | 'encrypted'
  | 'corrupt'
  | 'noPages'
  | 'emptyFile'
  // limits
  | 'fileTooLarge'
  | 'totalTooLarge'
  | 'tooManyPages'
  | 'tooManyFiles'
  // page range input
  | 'rangeEmpty'
  | 'rangeSyntax'
  | 'rangeZero'
  | 'rangeOutOfBounds'
  | 'rangeReversed'
  | 'rangeDuplicate'
  // editing / export
  | 'noFiles'
  | 'noPagesLeft'
  | 'noSelection'
  | 'exportFailed'
  | 'renderFailed';

export interface AppErrorParams {
  name?: string;
  token?: string;
  page?: number;
  pageCount?: number;
  limitMiB?: number;
  actualMiB?: number;
  limitPages?: number;
  actualPages?: number;
  limitFiles?: number;
  detail?: string;
}

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly params: AppErrorParams;

  constructor(code: AppErrorCode, params: AppErrorParams = {}, cause?: unknown) {
    super(`${code}${params.detail ? `: ${params.detail}` : ''}`);
    this.name = 'AppError';
    this.code = code;
    this.params = params;
    if (cause !== undefined) this.cause = cause;
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * Anything that escapes the core layer unexpectedly is still shown to the user as a
 * concrete message rather than a blank screen. `detail` keeps the original text for
 * the on-screen error box; it is never sent anywhere.
 */
export function toAppError(value: unknown, fallback: AppErrorCode): AppError {
  if (isAppError(value)) return value;
  const detail = value instanceof Error ? value.message : String(value);
  return new AppError(fallback, { detail }, value);
}
